import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaLeaf, FaHandshake, FaSeedling } from "react-icons/fa";

const pillars = [
  { icon: FaLeaf, title: "Women Empowered" },
  { icon: FaHandshake, title: "Fair Prices Paid" },
  { icon: FaSeedling, title: "Sustainable Farming" },
];

const stats = [
  { value: 1200, suffix: "+", label: "Women Farmers" },
  { value: 100, suffix: "%", label: "Fair Trade" },
  { value: 2020, suffix: "", label: "Supporting Communities", prefix: "Since " },
];

function useCountUp(target, start, duration = 1400) {
  const [n, setN] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let frame;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        const t0 = performance.now();
        const tick = (now) => {
          const p = Math.min(1, (now - t0) / duration);
          setN(Math.round(start + (target - start) * p));
          if (p < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        obs.disconnect();
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [target, start, duration]);

  return { n, ref };
}

const Stat = ({ value, suffix, label, prefix = "" }) => {
  const start = value > 100 ? Math.floor(value * 0.6) : 0;
  const { n, ref } = useCountUp(value, start);
  return (
    <div ref={ref} className="rounded-2xl bg-white/90 px-5 py-5 text-center shadow-sm backdrop-blur">
      <p className="font-display text-3xl text-espresso md:text-4xl">
        {prefix}
        {n.toLocaleString()}
        {suffix}
      </p>
      <p className="mt-1 text-sm font-medium text-cocoa">{label}</p>
    </div>
  );
};

const OurStory = () => {
  return (
    <section id="story" className="bg-cream-warm px-5 py-16 md:px-8 md:py-20 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="relative overflow-hidden rounded-[1.5rem]">
          <img
            src="/slide-farmer.png"
            alt="Rwandan woman coffee farmer with a basket of cherries"
            className="h-[22rem] w-full object-cover object-[center_22%] md:h-[32rem] lg:h-[36rem]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-espresso/75 via-espresso/20 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 max-w-xl text-cream md:bottom-10 md:left-10">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gold">
              Our Story
            </p>
            <h2 className="mt-2 font-display text-3xl leading-tight md:text-5xl">
              For the farmers.
              <br />
              For the future.
            </h2>
          </div>
          <div className="absolute right-5 top-5 flex h-28 w-28 flex-col items-center justify-center rounded-full border-2 border-gold bg-espresso/90 text-center text-cream shadow-lg md:right-8 md:top-8 md:h-32 md:w-32">
            <p className="font-display text-sm text-gold md:text-base">Direct Trade</p>
            <p className="mt-1 text-[10px] uppercase tracking-wide">Transparency</p>
          </div>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {stats.map((s) => (
            <Stat key={s.label} {...s} />
          ))}
        </div>

        <div className="mt-10 grid items-start gap-8 lg:grid-cols-[1.2fr_0.8fr]">
          <p className="max-w-2xl text-base leading-relaxed text-cocoa md:text-lg">
            KBL Coffee partners with smallholder women farmers in Rwanda to bring
            specialty Arabica from farm to cup—fair prices, sustainable farming, and
            a roast that honors every harvest.
          </p>
          <div className="flex flex-wrap gap-5">
            {pillars.map(({ icon: Icon, title }) => (
              <div key={title} className="flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-espresso text-gold">
                  <Icon />
                </div>
                <h3 className="text-sm font-semibold text-espresso">{title}</h3>
              </div>
            ))}
          </div>
        </div>

        <Link
          to="/about"
          className="mt-8 inline-flex rounded-md bg-espresso px-7 py-3 text-sm font-semibold text-cream transition hover:bg-espresso-soft"
        >
          Read Our Story
        </Link>
      </div>
    </section>
  );
};

export default OurStory;
